import { useCallback, useEffect, useRef, useState } from 'react';
import { setAudioSession } from './audioSession';

export type DictationState = 'idle' | 'recording' | 'transcribing';
export type DictationError = 'denied' | 'failed';

/** Auto-stop so a forgotten recording never runs into the 25 MB upload limit. */
const MAX_RECORDING_MS = 10 * 60 * 1000;

const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  return MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type));
}

export function dictationSupported(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
}

/**
 * Records the microphone and hands the recording to `transcribe`; the text goes to `onText`.
 * `cancel()` throws the recording away without sending it anywhere.
 */
export function useDictation({
  transcribe,
  onText,
}: {
  transcribe: (file: File, signal: AbortSignal) => Promise<string>;
  onText: (text: string) => void;
}) {
  const [state, setState] = useState<DictationState>('idle');
  const [error, setError] = useState<DictationError | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const chunks = useRef<Blob[]>([]);
  const cancelled = useRef(false);
  const abort = useRef<AbortController | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef({ transcribe, onText });
  latest.current = { transcribe, onText };

  const release = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    for (const track of stream.current?.getTracks() ?? []) track.stop();
    stream.current = null;
    recorder.current = null;
  }, []);

  /** Resolves true once the microphone records; false when it could not start. */
  const start = useCallback(async (): Promise<boolean> => {
    if (recorder.current || abort.current) return false;
    setError(null);
    let media: MediaStream;
    try {
      // Reading aloud may have set "playback"; recording needs iOS to pick the session again.
      setAudioSession('auto');
      media = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setError('denied');
      return false;
    }
    const mimeType = pickMimeType();
    const rec = new MediaRecorder(media, mimeType ? { mimeType } : undefined);
    stream.current = media;
    recorder.current = rec;
    chunks.current = [];
    cancelled.current = false;
    rec.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.current.push(event.data);
    };
    rec.onstop = async () => {
      const type = rec.mimeType || mimeType || 'audio/webm';
      release();
      if (cancelled.current || chunks.current.length === 0) {
        setState('idle');
        return;
      }
      const extension = type.includes('mp4') ? 'm4a' : 'webm';
      const file = new File(chunks.current, `dictation.${extension}`, { type });
      chunks.current = [];
      const controller = new AbortController();
      abort.current = controller;
      setState('transcribing');
      try {
        const text = (await latest.current.transcribe(file, controller.signal)).trim();
        if (!controller.signal.aborted && text) latest.current.onText(text);
      } catch {
        if (!controller.signal.aborted) setError('failed');
      } finally {
        abort.current = null;
        setState('idle');
      }
    };
    rec.start();
    setState('recording');
    timer.current = setTimeout(() => {
      if (rec.state === 'recording') rec.stop();
    }, MAX_RECORDING_MS);
    return true;
  }, [release]);

  const stop = useCallback(() => {
    if (recorder.current?.state === 'recording') recorder.current.stop();
  }, []);

  const cancel = useCallback(() => {
    cancelled.current = true;
    abort.current?.abort();
    if (recorder.current?.state === 'recording') recorder.current.stop();
    else setState('idle');
  }, []);

  // Leaving the page must not leave the microphone open.
  useEffect(
    () => () => {
      cancelled.current = true;
      abort.current?.abort();
      if (recorder.current?.state === 'recording') recorder.current.stop();
      release();
    },
    [release],
  );

  return { state, error, start, stop, cancel };
}
